import template from './sw-customer-detail-contacts.html.twig';
import './sw-customer-detail-contacts.scss';

const { Criteria } = Shopware.Data;
const { Classes } = Shopware;

const DUPLICATE_EMAIL_CODE = 'VIOLATION::CUSTOMER_EMAIL_NOT_UNIQUE';

Shopware.Component.register('sw-customer-detail-contacts', {
    template,

    inject: ['repositoryFactory'],

    props: {
        customerId: {
            type: String,
            required: false,
            default: null,
        },
        customer: {
            type: Object,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            contacts: [],
            isLoading: false,
            showContactModal: false,
            currentContact: null,
            contactEmailError: null,
            customerAddresses: [],
            salutations: [],
        };
    },

    computed: {
        contactRepository() {
            return this.repositoryFactory.create('tdcl_customer_contact');
        },

        customerAddressRepository() {
            return this.repositoryFactory.create('customer_address');
        },

        salutationRepository() {
            return this.repositoryFactory.create('salutation');
        },

        resolvedCustomerId() {
            return this.customerId
                || (this.customer && this.customer.id)
                || (this.$route && this.$route.params && this.$route.params.id);
        },

        columns() {
            return [
                {
                    property: 'email',
                    label: this.$tc('TopdataContactLoginSW6.admin.email'),
                    rawWidth: 200,
                },
                {
                    property: 'firstName',
                    label: this.$tc('TopdataContactLoginSW6.admin.name'),
                    rawWidth: 200,
                },
                {
                    property: 'active',
                    label: this.$tc('TopdataContactLoginSW6.admin.status'),
                    rawWidth: 100,
                },
            ];
        },
    },

    watch: {
        resolvedCustomerId: {
            immediate: true,
            handler() {
                this.loadContacts();
                this.loadCustomerAddresses();
                this.loadSalutations();
            },
        },
    },

    methods: {
        loadContacts() {
            if (!this.resolvedCustomerId) {
                return;
            }

            this.isLoading = true;

            const criteria = new Criteria();
            criteria.addFilter(Criteria.equals('customerId', this.resolvedCustomerId));
            criteria.addAssociation('defaultBillingAddress');
            criteria.addAssociation('defaultShippingAddress');

            this.contactRepository.search(criteria, Shopware.Context.api)
                .then((result) => {
                    this.contacts = result;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        loadCustomerAddresses() {
            if (!this.resolvedCustomerId) {
                return;
            }

            const criteria = new Criteria();
            criteria.addFilter(Criteria.equals('customerId', this.resolvedCustomerId));

            this.customerAddressRepository.search(criteria, Shopware.Context.api)
                .then((result) => {
                    this.customerAddresses = result;
                });
        },

        loadSalutations() {
            const criteria = new Criteria();
            this.salutationRepository.search(criteria, Shopware.Context.api)
                .then((result) => {
                    this.salutations = result;
                });
        },

        onAddContact() {
            this.contactEmailError = null;
            this.currentContact = this.contactRepository.create(Shopware.Context.api);
            this.currentContact.customerId = this.resolvedCustomerId;
            this.currentContact.active = true;
            this.showContactModal = true;
        },

        onEditContact(contact) {
            this.contactEmailError = null;
            this.currentContact = contact;
            this.showContactModal = true;
        },

        openContactDetail(contact) {
            if (!contact || !contact.id) {
                return;
            }

            this.$router.push({
                name: 'topdata.contact.login.detail',
                params: { id: contact.id },
            });
        },

        onDeleteContact(contact) {
            this.$refs.contactDeleteModal.showDeleteModal(contact);
        },

        onConfirmDeleteContact(contact) {
            this.isLoading = true;

            this.contactRepository.delete(contact.id, Shopware.Context.api)
                .then(() => {
                    this.loadContacts();
                    this.createNotificationSuccess({
                        message: this.$tc('TopdataContactLoginSW6.admin.contactDeleted'),
                    });
                })
                .catch(() => {
                    this.isLoading = false;
                    this.createNotificationError({
                        message: this.$tc('TopdataContactLoginSW6.admin.contactDeleteError'),
                    });
                });
        },

        onSaveContact() {
            this.isLoading = true;

            this.contactRepository.save(this.currentContact, Shopware.Context.api)
                .then(() => {
                    this.contactEmailError = null;
                    this.showContactModal = false;
                    this.loadContacts();
                    this.createNotificationSuccess({
                        message: this.$tc('TopdataContactLoginSW6.admin.contactSaved'),
                    });
                })
                .catch((exception) => {
                    this.isLoading = false;
                    this.handleSaveError(exception);
                });
        },

        handleSaveError(exception) {
            const errors = exception && exception.response && exception.response.data
                ? exception.response.data.errors
                : [];

            const emailViolation = errors.find((error) => (
                error.code === DUPLICATE_EMAIL_CODE
                || (error.source && error.source.pointer === '/email')
            ));

            if (emailViolation) {
                this.contactEmailError = new Classes.ShopwareError({
                    code: DUPLICATE_EMAIL_CODE,
                    detail: this.$tc('TopdataContactLoginSW6.admin.contactEmailNotUnique'),
                });

                this.createNotificationError({
                    message: this.$tc('TopdataContactLoginSW6.admin.contactEmailNotUnique'),
                });

                return;
            }

            this.contactEmailError = null;
            this.createNotificationError({
                message: this.$tc('TopdataContactLoginSW6.admin.contactSaveError'),
            });
        },

        onCloseModal() {
            this.contactEmailError = null;
            this.showContactModal = false;
            this.currentContact = null;
        },

        getAddressLabel(address) {
            if (!address) {
                return this.$tc('TopdataContactLoginSW6.admin.noAddress');
            }
            return `${address.street}, ${address.zipcode} ${address.city}`;
        },
    },
});
